// src/lib/missions/model.ts
import type { MissionReward, MissionState, MissionView } from '@/types/quest';

/**
 * Logica pura della sezione Missioni: normalizzazione, ordinamento, derivazioni.
 *
 * Sta qui, separata dai componenti e priva di dipendenze (nessun React, nessuna rete, nessun
 * `Date.now()` implicito — l'istante entra sempre come parametro), perche' e' la parte con il
 * rischio di correttezza piu' alto ed e' l'unica verificabile OGGI: `X-Dev-Bypass` e' inerte su
 * Quest in produzione e l'utente Logto di test non esiste ancora, quindi il percorso reale non e'
 * esercitabile. Se una cosa qui e' sbagliata, nessun test di integrazione la prendera' per mesi.
 */

/** Lo stato dichiarato dal servizio, piu' il ramo per un valore che non conosciamo. */
export type EffectiveState = MissionState | 'unknown';

const KNOWN_STATES: readonly MissionState[] = ['active', 'completed', 'claimed', 'expired'];

function parseState(raw: string): EffectiveState {
  return (KNOWN_STATES as readonly string[]).includes(raw) ? (raw as MissionState) : 'unknown';
}

/** `expiresAt` in millisecondi, o `null` se assente o non interpretabile. */
export function expiryMs(mission: Pick<MissionView, 'expiresAt'>): number | null {
  if (mission.expiresAt === null) return null;
  const ms = Date.parse(mission.expiresAt);
  // Una data illeggibile NON e' "non scade mai": sarebbe la lettura piu' generosa possibile di un
  // dato rotto. Trattarla come assente e' l'unica scelta che non promette nulla all'utente.
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Lo stato che l'utente deve vedere — che non sempre e' `mission.state`.
 *
 * Il campo `state` e' persistito, e a portarlo a `expired` e' uno sweeper periodico
 * (`quest-api/src/workers/expirySweep.ts`). Fra la scadenza e il passaggio dello sweeper esiste una
 * finestra in cui il documento dice ancora `completed` mentre `canClaim` lato server e' gia' falso
 * (`quest-api/src/domain/states.ts:5-7`, che confronta con l'istante ora). Mostrare "riscuotibile"
 * in quella finestra e' una promessa che il server rifiutera': qui la scadenza vince sullo stato.
 *
 * `claimed` e' l'eccezione e non e' simmetrica: una ricompensa gia' pagata resta pagata, la
 * scadenza successiva non la annulla.
 */
export function effectiveState(mission: MissionView, now: Date): EffectiveState {
  const state = parseState(mission.state);
  if (state === 'claimed' || state === 'expired' || state === 'unknown') return state;
  const expiry = expiryMs(mission);
  if (expiry !== null && now.getTime() >= expiry) return 'expired';
  return state;
}

/**
 * Riscuotibile *adesso*, secondo le stesse condizioni del server (`domain/states.ts:5-7`):
 * solo da `completed`, e solo prima dell'istante di scadenza.
 *
 * 🔴 DAL 30/8 UN BOTTONE LA USA, ed e' l'unico predicato che decide se compare. Era gia' scritto
 * che la fase 2 sarebbe dovuta partire da qui «e non da una seconda copia della stessa regola»: la
 * copia non e' stata scritta, e `missionGroup` restituisce `'claimable'` esattamente su questo
 * predicato — invariante asserito nel verificatore, perche' due regole che devono coincidere e
 * vivono in due funzioni sono una promessa che nessuno mantiene a memoria.
 *
 * ⚠️ Non e' una garanzia sul server, e' una guardia sul GESTO: fra questa lettura e la risposta
 * l'istanza puo' essere scaduta o gia' riscossa altrove. Per questo ogni esito ha una faccia
 * (`@/lib/missions/claim`) e nessuno di essi si affida al fatto che questa funzione avesse ragione.
 */
export function isClaimable(mission: MissionView, now: Date): boolean {
  return effectiveState(mission, now) === 'completed';
}

/**
 * Avanzamento in [0, 1].
 *
 * Due difese, entrambe per casi che il servizio puo' produrre davvero: un `target` <= 0 renderebbe
 * il rapporto infinito o NaN (e una barra di larghezza `NaN%` sparisce senza errori), e `progress`
 * puo' SUPERARE `target` — l'ingest somma gli eventi e il completamento e' una soglia, non un
 * tetto, quindi 5 battaglie su un obiettivo da 3 e' uno stato normale, non un difetto.
 */
export function progressRatio(mission: Pick<MissionView, 'progress' | 'target'>): number {
  if (!Number.isFinite(mission.target) || mission.target <= 0) return 0;
  if (!Number.isFinite(mission.progress) || mission.progress <= 0) return 0;
  return Math.min(1, mission.progress / mission.target);
}

/** `progress` limitato a `target` per la resa testuale ("3/3", mai "5/3"). */
export function displayProgress(mission: Pick<MissionView, 'progress' | 'target'>): number {
  if (!Number.isFinite(mission.progress) || mission.progress <= 0) return 0;
  if (!Number.isFinite(mission.target) || mission.target <= 0) return mission.progress;
  return Math.min(mission.progress, mission.target);
}

/** Discrimina l'unione `reward`. Non usa `rewardEssences`, che e' il campo legacy. */
export function isCosmeticReward(
  reward: MissionReward,
): reward is { cosmetic: { catalogRef: string } } {
  return typeof reward === 'object' && reward !== null && 'cosmetic' in reward;
}

/** Le essenze della ricompensa, o `null` se la ricompensa e' un cosmetico. */
export function rewardEssences(mission: Pick<MissionView, 'reward'>): number | null {
  return isCosmeticReward(mission.reward) ? null : mission.reward.essences;
}

/** Il riferimento a catalogo del cosmetico, o `null` se la ricompensa e' in essenze. */
export function rewardCatalogRef(mission: Pick<MissionView, 'reward'>): string | null {
  return isCosmeticReward(mission.reward) ? mission.reward.cosmetic.catalogRef : null;
}

/**
 * Millisecondi mancanti alla scadenza; `null` se non scade (traguardo) e `0` se e' gia' passata.
 * Mai negativo: un "-3h" a schermo e' un difetto, non un'informazione.
 */
export function timeLeftMs(mission: MissionView, now: Date): number | null {
  const expiry = expiryMs(mission);
  if (expiry === null) return null;
  return Math.max(0, expiry - now.getTime());
}

/**
 * Ordine di priorita' della riga. Piu' basso = piu' in alto nella lista.
 *
 * ⚠️ NATO PER UN MOTIVO CHE NON VALE PIU'. Questo rango esisteva perche' il raggruppamento era
 * impossibile (`windowKey` non era proiettato) e l'ordine era l'unico modo che la lista aveva di
 * dire cosa conta. Da quando le righe sono PARTIZIONATE per vista (v. `MissionGroup`), il rango
 * non decide piu' cosa risalta: decide solo l'ordine DENTRO un gruppo, dove i valori sono quasi
 * sempre pari — quindi in pratica lavorano il progresso e il tie-break.
 *
 * Si tiene lo stesso perche' e' l'unica cosa che ordina la coda (`expired` + `unknown` insieme) e
 * perche' toglierlo non guadagna nulla. Non si tiene come "la lista sa cosa conta": non e' piu' lui
 * a saperlo.
 */
const RANK: Record<EffectiveState, number> = {
  completed: 0, // riscuotibile: la sola riga su cui l'utente puo' agire — dal 30/8 davvero
  active: 1, // in corso
  claimed: 2, // gia' incassata: memoria, non azione
  expired: 3, // persa
  unknown: 4, // stato che non conosciamo: in fondo, mai nascosta
};

/**
 * La lista ordinata: prima cio' su cui si puo' agire, poi cio' che e' in corso (piu' vicino al
 * traguardo per primo), infine cio' che e' concluso. A parita', `instanceId`.
 *
 * 🔴 IL TIE-BREAK SU `instanceId` E' LA PARTE CHE NON SI TOGLIE. Rende l'ordine TOTALE, quindi
 * stabile fra un refetch e l'altro: senza, due righe di pari rango si scambiano di posto a ogni
 * aggiornamento — cioe' sotto le dita di chi sta guardando. Con `refetchOnFocus` attivo sul client
 * Quest succede a ogni ritorno sull'app, non di rado.
 *
 * Non muta l'array in ingresso (RTK Query espone lo stato della cache: ordinarlo sul posto
 * significherebbe scrivere dentro lo store).
 */
export function sortMissions(missions: readonly MissionView[], now: Date): MissionView[] {
  return [...missions].sort((a, b) => {
    const byRank = RANK[effectiveState(a, now)] - RANK[effectiveState(b, now)];
    if (byRank !== 0) return byRank;
    const byProgress = progressRatio(b) - progressRatio(a);
    if (byProgress !== 0) return byProgress;
    return a.instanceId.localeCompare(b.instanceId);
  });
}

/**
 * L'unita' con cui mostrare il tempo rimanente, scelta qui e non nel componente: la scelta e' una
 * regola (quale soglia fa passare da ore a giorni), la traduzione e' presentazione. Tenerle
 * separate e' cio' che rende questa parte verificabile senza montare React.
 *
 * Sempre arrotondata per DIFETTO: "scade fra 2 ore" con 2h59m residue e' vero e prudente; per
 * eccesso prometterebbe piu' tempo di quanto ce ne sia, su una finestra oltre la quale la
 * ricompensa e' persa.
 */
export type TimeLeftParts =
  | { unit: 'days' | 'hours' | 'minutes'; count: number }
  | { unit: 'soon' };

const MINUTE = 60_000, HOUR = 3_600_000, DAY = 86_400_000;

export function timeLeftParts(ms: number): TimeLeftParts {
  if (!Number.isFinite(ms) || ms < MINUTE) return { unit: 'soon' };
  if (ms >= DAY) return { unit: 'days', count: Math.floor(ms / DAY) };
  if (ms >= HOUR) return { unit: 'hours', count: Math.floor(ms / HOUR) };
  return { unit: 'minutes', count: Math.floor(ms / MINUTE) };
}

/* ------------------------------------------------------------------------- */
/* Le tre viste chieste da Luca (2026-08-30), piu' la coda                     */
/* ------------------------------------------------------------------------- */

/**
 * I gruppi in cui la sezione partiziona le missioni.
 *
 * Le tre viste dell'ordine di Luca — *«tutte le quest aperte, quelle in fase di avanzamento utente
 * e quelle completate con data/premio»* — piu' due: la CODA, e, dal 30/8, il gruppo RISCUOTIBILE.
 *
 * 🔴 `claimable` E' LA MAPPA RIDISCUSSA, NON ALLARGATA PER ABITUDINE. Nella v1 le istanze
 * `completed` stavano insieme alle `claimed` sotto «Completate», e la ragione era scritta con la
 * propria data di scadenza addosso: *«quel gruppo non offrirebbe alcuna azione, sarebbe una
 * gerarchia visiva che promette un gesto assente… quando il claim arrivera', quella lettura
 * tornera' la piu' forte e questa mappa va RIDISCUSSA, non ereditata»* (micro-design §2). Il claim
 * e' arrivato. Da ora `completed` = «Da riscuotere» e `claimed` = «Riscosse»: due fatti diversi che
 * ora hanno anche due comportamenti diversi — su uno c'e' un bottone, sull'altro no — e tenerli
 * nello stesso gruppo significherebbe mettere accanto le righe su cui si puo' agire e quelle su cui
 * non resta nulla da fare. E' anche cio' che fa MIGRARE la riga dopo la riscossione: senza questo
 * taglio, incassare non avrebbe spostato nulla.
 *
 * ⚠️ PERCHE' `expired` NON STA IN NESSUNA DELLE VISTE. Un'intestazione di sezione e'
 * un'affermazione su ogni riga che contiene. «In avanzamento» su una missione scaduta e' falso (non
 * avanza piu'); «Riscosse» su una missione persa e' falso; nasconderla e' un'omissione — e il
 * design del 25/8 rifiuta esplicitamente di nascondere lo stato. Delle quattro strade tre dicono
 * una cosa non vera: resta la coda. Stessa sorte per `unknown`, il ramo tenuto per uno stato che
 * Quest aggiungesse domani — in fondo, mai nascosto.
 */
export type MissionGroup = 'claimable' | 'open' | 'inProgress' | 'claimed' | 'closed';

/**
 * Il gruppo di una missione. Totale per costruzione: ogni riga cade in uno e uno solo.
 *
 * Legge `effectiveState` e MAI `mission.state` grezzo. La differenza morde esattamente qui: fra la
 * scadenza reale e il passaggio dello sweeper il documento dice ancora `completed` mentre il server
 * rifiuterebbe il claim, quindi con lo stato grezzo quella riga finirebbe fra le «completate» —
 * cioe' fra le cose andate bene — mentre e' persa.
 */
export function missionGroup(mission: MissionView, now: Date): MissionGroup {
  const state = effectiveState(mission, now);
  // 🔴 `completed` e `claimed` erano lo stesso gruppo fino al 29/8 e ora non lo sono piu': la
  // prima e' l'unica riga su cui esiste un gesto, la seconda e' memoria. Il predicato e' lo stesso
  // di `isClaimable` — deliberatamente, e il verificatore lo asserisce come invariante: il bottone
  // deve comparire ESATTAMENTE dove compare questo gruppo, altrimenti esiste una riga in «Da
  // riscuotere» senza bottone (l'esatto difetto che questo lavoro chiude) o un bottone altrove.
  if (state === 'completed') return 'claimable';
  if (state === 'claimed') return 'claimed';
  if (state === 'active') {
    // La soglia e' `> 0`, non `>= 1`: `progress` e' un intero contato dal backend, ma un valore
    // negativo o non finito arrivato da un dato rotto NON deve valere "iniziata". Chi legge questa
    // riga come "progress > 0 e' ovvio" ha ragione sul caso normale e torto su quello rotto.
    return Number.isFinite(mission.progress) && mission.progress > 0 ? 'inProgress' : 'open';
  }
  return 'closed'; // 'expired' | 'unknown'
}

/** Le quattro liste, ognuna gia' ordinata. Un solo passaggio sull'ordinamento, non quattro. */
export interface GroupedMissions {
  claimable: MissionView[];
  open: MissionView[];
  inProgress: MissionView[];
  claimed: MissionView[];
  closed: MissionView[];
}

/**
 * Partiziona e ordina in un colpo solo.
 *
 * Ordina PRIMA e distribuisce POI: `sortMissions` produce un ordine totale sull'intero insieme, e
 * una partizione che preserva l'ordine di scorrimento lo eredita dentro ogni gruppo. Ordinare i
 * quattro gruppi separatamente darebbe lo stesso risultato al prezzo di quattro sort — e di un
 * secondo posto in cui il tie-break potrebbe essere dimenticato.
 */
export function groupMissions(missions: readonly MissionView[], now: Date): GroupedMissions {
  const out: GroupedMissions = { claimable: [], open: [], inProgress: [], claimed: [], closed: [] };
  for (const mission of sortMissions(missions, now)) {
    out[missionGroup(mission, now)].push(mission);
  }
  return out;
}

/**
 * Quale data la riga puo' mostrare, e — inseparabilmente — QUALE FATTO la regge.
 *
 * Non torna un numero nudo di proposito. Le due date rispondono a due domande diverse, e una
 * stringa che ne stampasse una col nome dell'altra sarebbe falsa **proprio dove costa di piu'**:
 *
 *  - `completedAt` → «hai completato la missione il …». Vero e definitivo.
 *  - `claimedAt`   → «hai RISCOSSO il …», cioe' **quando l'hai CHIESTO**. 🔴 NON «hai ricevuto»:
 *    la data viene stampigliata anche quando il trasferimento QBank torna `retryable`/`failed` —
 *    il premio e' DOVUTO e in carico al re-drive, ma **non e' arrivato**. Lo stato di consegna vive
 *    su `Payout` e **non e' esposto a nessuna rotta giocatore** (ADR-0073). ⇒ una copy «premio
 *    ricevuto il {data}» sarebbe una promessa che nulla potra' mai smentire. Al massimo
 *    «riscossa il».
 *
 * Restituire il `kind` insieme al valore e' cio' che impedisce alla chiamante di scegliere la
 * stringa sbagliata: non puo' rendere la data senza sapere di quale delle due si tratta.
 *
 * 📋 CONTRATTO (fissato dal produttore — cantiere `quest1`, 2026-08-29, 382/382 asserzioni verdi):
 * `completedAt: string | null` e `claimedAt: string | null`, ISO-8601 UTC coi millisecondi, stessa
 * serializzazione di `expiresAt`; **chiave sempre presente**, `null` quando non applicabile.
 * Invarianti per stato: `active` → entrambi null · `completed` → `completedAt` valorizzato e
 * `claimedAt` null · `claimed` → entrambi, con `claimedAt >= completedAt`.
 * Qui i due campi restano comunque **opzionali**: assente e `null` si leggono uguali, quindi questo
 * codice e' gia' giusto sia PRIMA sia DOPO il deploy della proiezione — si accende sul dato, senza
 * flag e senza un secondo rilascio.
 *
 * 🔴 `expiresAt` NON e' l'alternativa quando i campi mancano. Al completamento viene ri-timbrato
 * con i 7 giorni di riscossione: usarlo come data di completamento avrebbe mostrato una data nel
 * FUTURO. Plausibile su ogni riga e giusto su nessuna — il modo peggiore in cui un dato puo'
 * essere sbagliato. Se il campo non c'e', la data NON si mostra.
 */
export type CompletionStamp = { kind: 'completed' | 'claimed'; ms: number };

function parseStamp(raw: unknown): number | null {
  if (typeof raw !== 'string') return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : ms;
}

export function completionStamp(mission: MissionView): CompletionStamp | null {
  // `claimed` per primo: su una riga riscossa e' il fatto piu' recente e quello che il giocatore
  // associa alla riga. L'invariante del produttore (`claimedAt >= completedAt`) lo conferma.
  const claimed = parseStamp(mission.claimedAt);
  if (claimed !== null) return { kind: 'claimed', ms: claimed };
  const completed = parseStamp(mission.completedAt);
  if (completed !== null) return { kind: 'completed', ms: completed };
  return null;
}

/**
 * Vero se la ricompensa di questa riga va MOSTRATA come cifra.
 *
 * Non e' una scelta estetica. Una missione scaduta ha un `reward` valorizzato nello snapshot — il
 * premio che avrebbe dato — e stamparlo accanto a una riga persa mostra al giocatore una cifra che
 * non ricevera' mai. Il campo c'e'; il fatto che lo regge, no.
 */
export function showsReward(mission: MissionView, now: Date): boolean {
  return missionGroup(mission, now) !== 'closed';
}

/**
 * Vero se accanto al titolo va scritta la parentesi «(non ancora attiva)».
 *
 * Ordine di Luca (2026-08-31): *«lasciale visibili, ma tra parentesi indica inattive»*. Il fatto
 * che lo motiva: Quest ha 13 missioni attive e il giocatore ne puo' completare 3 — le altre
 * aspettano un evento che nessun servizio emette ancora. Tutte uguali a schermo, chi ne prova una
 * che non puo' avanzare conclude che l'app e' rotta.
 *
 * 🔴 DUE CANCELLI, E IL SECONDO PROTEGGE DEL DENARO.
 *
 *  1. **Confronto POSITIVO con `'not-yet'`, mai un ternario.** Assente ⇒ falso. `'playable'` ⇒
 *     falso. Un valore che non conosciamo ⇒ falso. La differenza fra questa forma e
 *     `availability === 'playable' ? … : …` e' l'intero rischio del campo: il secondo manda
 *     l'ignoto e l'ASSENTE in uno dei due rami, e l'assenza e' esattamente cio' che produce un
 *     rollback di Quest a un'immagine precedente a `c487bf0`. Il campo non ha validazione a
 *     runtime (`getMissions` e' un cast diretto), quindi «ignoto» non e' un caso di scuola.
 *  2. **Solo su una riga che il giocatore vede come `active`.** Il servizio gia' omette il campo
 *     su `completed`/`claimed`/`expired` (`missions.service.ts:146` @ `c487bf0`), e questa
 *     condizione e' la SECONDA chiusura dello stesso percorso — l'unica che non dipende da come
 *     il servizio decide di proiettare. Se un giorno una riga `completed` uscisse `'not-yet'`,
 *     scriveremmo «non ancora attiva» accanto a un premio riscuotibile: il giocatore ci crede,
 *     non preme, e in sette giorni la ricompensa scade. Nessun errore e nessun log.
 *
 * ⚠️ Il cancello 2 legge `effectiveState`, NON `mission.state`. Non e' pedanteria di stile: su una
 * riga `active` gia' scaduta per orologio, `mission.state` dice ancora `'active'` finche' lo
 * sweeper non passa, e la parentesi comparirebbe accanto a «Tempo scaduto». `effectiveState` e'
 * lo stato che il giocatore sta LEGGENDO, ed e' l'unico con cui questa etichetta deve concordare.
 *
 * ⚠️ La misura lato Quest sbaglia — e sbaglia VERSO IL SILENZIO, che e' la direzione buona. Sui
 * dati del 31/8 etichetta correttamente 9 missioni su 10: `w.tournaments3` risulta `'playable'`
 * e non lo e' (un collaudo del 27/8 ha lasciato un progresso, e la sonda lo legge come «qualcuno
 * l'ha avanzata» — `availability.service.ts`, limite 2). Quel difetto e' una riga di DATO in
 * produzione, non un difetto di questa funzione: compensarlo qui significherebbe scrivere lato
 * client una tabella di eccezioni che nessuno togliera' il giorno che il monolite emette davvero.
 */
export function showsNotYetAvailable(mission: MissionView, now: Date): boolean {
  if (effectiveState(mission, now) !== 'active') return false;
  return mission.availability === 'not-yet';
}
